import { useContext, useEffect, useMemo } from 'react';
import { SmartSendContext } from '../../lib/context/SmartSendContext';
import { SmartSendSummary, SmartSendFilterContext, FilterName } from '../../lib/helpers/SmartSendFilterApplier';
import { UpdateFilterAction } from '../../lib/context/SmartSendContextModels';
import Book from './actions/book/Book';
import { SmartSendItemGroup } from './item/SmartSendGroup';
import SoldItemsList from './SoldItemsList';
import { getNamedFilter } from '../../lib/helpers/DefaultFilters';
import { KeyedSmartSendGroupCache } from '../../lib/cache/SmartSendGroupCache';
import { SmartSendSubType } from '../../lib/consts/SmartSendSubType';
import { isFixSpecificIssue } from './isFixSpecificIssue';

interface props {
  isXs: boolean;
}

interface innerprops extends props {
  filterKey: string;
  groups: SmartSendItemGroup[];
  filter: SmartSendFilterContext;
  summary: SmartSendSummary;
  updatePaging: (page: number, pageSize: number) => void;
}

const SoldItemsInner: React.FC<innerprops> = (props) => {
  return (
    <>
      <SoldItemsList
        filterKey={props.filterKey}
        groups={props.groups}
        filter={props.filter}
        summary={props.summary}
        isXs={props.isXs}
        setPaging={props.updatePaging}
      />
      <Book isXs={props.isXs} filterKey={props.filterKey} />
    </>
  );
};

const SoldItems: React.FC<props> = (props) => {
  const filterKey = SmartSendSubType;
  const ctx = useContext(SmartSendContext);

  // Ensure the filter is set to the correct default
  useEffect(() => {
    const defaultName: FilterName = 'Sold';
    const applicableFilters: FilterName[] = [
      'DEFAULT',
      defaultName,
      'Ready',
      'NotReady',
      'Issues',
      'SavedForLater',
      'Important',
      'Groupable',
      'Sold_Mobile',
      'Post_Create'
    ];

    if (filterKey !== SmartSendSubType) {
      return;
    }

    // If its not one of the applicable filters, reset it to the default
    if (!applicableFilters.includes(ctx.state.filters[filterKey]?.filter?.CurrentFilter?.Name)) {
      ctx.dispatch(
        new UpdateFilterAction(filterKey, {
          ...ctx.state.filters[filterKey].filter,
          CurrentFilter: getNamedFilter(defaultName)
        })
      );
    }

    // If its the issues filter, but there are no issues, reset it to the default
    if (
      isFixSpecificIssue(ctx.state.filters[filterKey]?.filter) &&
      ctx.state.filters[filterKey]?.summary.stats['NotReady'] === 0
    ) {
      ctx.dispatch(
        new UpdateFilterAction(filterKey, {
          ...ctx.state.filters[filterKey].filter,
          CurrentFilter: getNamedFilter(defaultName)
        })
      );
    }
  }, [ctx.state.filters, filterKey, ctx]);

  const updatePaging = (skip: number, take: number) => {
    const u = { ...ctx.state.filters[filterKey].filter };
    u.CurrentFilter.Take = take;
    u.CurrentFilter.Skip = skip;
    ctx.dispatch(new UpdateFilterAction(filterKey, u));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groups = useMemo(() => KeyedSmartSendGroupCache[filterKey] ?? [], [KeyedSmartSendGroupCache[filterKey]]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filter = useMemo(() => ctx.state.filters[filterKey].filter, [ctx.state.filters[filterKey].filter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const summary = useMemo(() => ctx.state.filters[filterKey].summary, [ctx.state.filters[filterKey].summary]);

  return (
    <SoldItemsInner
      filterKey={filterKey}
      groups={groups}
      updatePaging={updatePaging}
      summary={summary}
      filter={filter}
      isXs={props.isXs}
    />
  );
};

export default SoldItems;
